import React, { useEffect, useState } from 'react';
import Accordion from './Accordian';
import axios from 'axios';
import { FaStar } from "react-icons/fa";
import { BusinessData, Staff } from '../../types/types';
import { useNavigate } from 'react-router-dom';

type FAQItem = {
  question: string;
  answer: JSX.Element | string;
};

type CategoryFAQ = {
  category: string;
  items: FAQItem[];
};

interface FaqProps {
  businessData: BusinessData;
  get_type: string;
  get_location: string;
  get_services: { name: string }[];
  scrollToServices: () => void;
  scrollToBusinessHours: () => void;
}

const Faq: React.FC<FaqProps> = ({
  businessData,
  get_type,
  get_services,
  get_location,
  scrollToServices,
  scrollToBusinessHours
}) => {
  const $businessName = businessData.business_name;
  const $services = get_services.map((service) => service.name).join(', ');
  const $location = businessData.address || get_location;
  const $businessType = get_type;
  const $businessHours =  "Mon-Fri, 10am - 8pm";

  const categorizedFaqData: CategoryFAQ[] = [
    {
      category: "General Salon & Spa FAQs",
      items: [
        {
          question: `What services do you offer at ${$businessName}?`,
          answer: `At ${$businessName}, we provide a wide range of beauty and wellness services, including ${$services}. Our expert stylists and beauticians ensure top-quality results. Whether you're looking for a quick touch-up or a complete makeover, our top salon in ${$location} offers the best experience. Book an appointment today and explore our premium salon services with great customer reviews!`
        },
        {
          question: "Do I need to book an appointment, or do you accept walk-ins?",
          answer: (
            <>
              We recommend booking an appointment for a seamless salon experience, but we do accept walk-ins based on availability. Using our salon management software, you can easily schedule a slot at {$businessName}, ensuring you get the best service without waiting. Our team is dedicated to providing a smooth and relaxing visit. You can book our{' '}
              <span onClick={scrollToServices} className="text-blue-500 cursor-pointer underline">services</span> in just 3 clicks at Feelaxo. Walk in or book online today!
            </>
          )
        },
        {
          question: "How can I book an appointment?",
          answer: `Booking an appointment is simple! Visit our website, use our salon booking software, or call us directly. Our easy-to-use system lets you select your preferred service, stylist, and time slot. With our advanced salon management software, you can book, reschedule, or cancel with just a few clicks. Experience a hassle-free way to book your ${$businessType} services at ${$businessName}!`
        },
        {
          question: "What should I do if I need to cancel or reschedule my appointment?",
          answer: "If you need to cancel or reschedule, please do so at least 24 hours in advance via our salon booking system or by calling us. Our goal is to provide the best service while accommodating every client's needs. You can log in to your account and cancel the appointment on the 'My Booking' page. We ensure flexible and customer-friendly booking management."
        },
        {
          question: "What are your salon hours?",
          answer: (
            <>
              We are open {$businessHours} to provide flexible appointment options. Whether you need a morning beauty session or an evening relaxation treatment, we're here to serve you. Please check our {' '}
              <span onClick={scrollToBusinessHours} className="text-blue-500 cursor-pointer underline">
                Opening Hours
              </span> or call us for the most up-to-date schedule.
            </>
          )
        },
        {
          question: "Where are you located, and is parking available?",
          answer: `Our salon is located at ${$location} with convenient access and ample parking for all customers. Public parking is available at the location.`
        },
      ]
    },
    {
      category: "Salon & Spa Services",
      items: [
        {
          question: `Do you offer services for men, women, and children?`,
          answer: `Yes! ${$businessName} provides specialized services for men, women, and children, including haircuts, styling, and grooming treatments. Our expert stylists ensure every client gets a tailored experience. Check our list of ${$businessType} services and book an appointment.`
        },
        {
          question: "What are your prices for different services?",
          answer: (
            <>
              Our pricing varies based on the service and package you choose. We offer affordable salon services with exclusive deals and offers. Please refer to our {' '}
              <span onClick={scrollToServices} className="text-blue-500 cursor-pointer underline">Pricing Menu</span> on the website or contact us directly for details.
            </>
          )
        },
        {
          question: "Do you offer any discounts or special packages?",
          answer: `Yes! We regularly offer salon deals and discounts on hairstyling, facials, and spa treatments. Follow us on social media or inquire at the ${$businessType} for current offers. We also send regular offer updates to clients on WhatsApp and email. To get regular updates, please subscribe.`
        },
        {
          question: "What forms of payment do you accept?",
          answer: "We accept cash, credit/debit cards, UPI, and digital payments like Gpay. Our salon management software ensures secure transactions and a seamless checkout experience."
        },
        {
          question: "Is a deposit required for booking certain services?",
          answer: "Yes, some premium services require a deposit to confirm your appointment. Our salon booking software makes payment easy and secure. We'll inform you at the time of booking if a deposit is needed."
        },
      ]
    },
    {
      category: "Hair & Beauty Treatment FAQs",
      items: [
        {
          question: "How long will my hair appointment take?",
          answer: (
            <>
              Service times vary; haircuts take around 30 minutes, while treatments like keratin or coloring may take 2-3 hours. Please{' '}
              <span onClick={scrollToServices} className="text-blue-500 cursor-pointer underline">Check the service</span>{' '}
              duration when booking for an estimated time.
            </>
          )
        },
        {
          question: "Do you offer consultations for hair services?",
          answer: `Yes, we provide personalized hair consultations. Our experts analyze your hair type and suggest the best treatments at ${$businessName}.`
        },
        {
          question: "Can I bring my own hair products for use during my service?",
          answer: `Yes! If you prefer specific products, we're happy to accommodate your request at ${$businessName}. We aim to provide the best ${$businessType} experience.`
        },
        {
          question: "What if I'm not satisfied with my hair service?",
          answer: "Your satisfaction is our priority. If you're not happy, let us know within 7 days, and we'll work to address your concerns!"
        },
        {
          question: "How often should I come in for maintenance or touch-ups?",
          answer: "For best results, visit every 4-6 weeks for touch-ups, haircuts, and skincare treatments. This depends on your style and hair type; we're happy to provide recommendations after your service."
        },
      ]
    },
    {
      category: "Skincare & Makeup Services",
      items: [
        {
          question: "What skincare or facial treatments do you offer?",
          answer: (
            <>
              We offer hydrating facials, anti-aging treatments, and deep cleansing facials. Check our {' '}
              <span onClick={scrollToServices} className="text-blue-500 cursor-pointer underline">list of services</span>{' '}
              on the website or call us to learn more about our various facials, peels, and skin treatments.
            </>
          )
        },
        {
          question: "Do you offer makeup services, including bridal or special occasion makeup?",
          answer: `Yes, we provide expert bridal makeup, party makeup, and event styling. Choose ${$businessName} for your next special occasion!`
        },
        {
          question: "Do you offer hot waxing services?",
          answer: `Yes, we provide full-body waxing, Brazilian wax, and skin-sensitive treatments at our top salon.`
        },
        {
          question: "Are there any post-care instructions for skincare or waxing treatments?",
          answer: `Yes, we offer detailed aftercare advice to ensure the best results. Avoid sun exposure and hot showers for 24 hours post-treatment, and follow any additional guidelines provided by our experts at ${$businessName}.`
        },
      ]
    },
    {
      category: "Hygiene & Safety",
      items: [
        {
          question: "What are your COVID-19 safety protocols?",
          answer: "We follow strict sanitization, social distancing, and contactless payments to ensure a safe salon experience. We follow local health guidelines and require masks as necessary."
        },
        {
          question: "Are all tools and equipment sanitized between clients?",
          answer: "Absolutely! Hygiene is our priority, and we disinfect all tools after every client visit. We maintain strict hygiene standards for your safety."
        },
        {
          question: "Do you accommodate clients with allergies or skin sensitivities?",
          answer: "Yes, we use hypoallergenic products and offer patch tests to ensure your safety. Please inform us of any sensitivities, and we will adjust products and services accordingly."
        },
      ]
    },
    {
      category: "Event & Mobile Services",
      items: [
        {
          question: "Do you offer bridal packages or event styling?",
          answer: `Yes! Our bridal packages include makeup, hairstyling, and pre-wedding beauty treatments. We offer customized bridal and event packages, including on-site styling at ${$businessName}.`
        },
        {
          question: "Can I book multiple services in one appointment?",
          answer: "Absolutely! Get hair, skincare, and nail treatments in one visit for the ultimate salon experience. We're happy to coordinate multiple services for your convenience."
        },
        {
          question: "Do you offer hair or beauty services at the client's location or home?",
          answer: `Yes, we offer on-location salon services for special events and bridal styling. Book your home service today with ${$businessName}!`
        },
      ]
    },
  ];

  return (
    <div className="faq-container">
      {categorizedFaqData.map((category, categoryIndex) => (
        <div key={categoryIndex} className="mb-6">
          <h3 className="text-xl font-bold mb-3 text-primary-600 border-b pb-2">
            {category.category}
          </h3>
          <div className="space-y-2">
            {category.items.map((faq, index) => (
              <div 
                key={index} 
                className="border rounded-lg p-2 bg-white shadow-sm hover:shadow-md transition-shadow duration-200"
              >
                <Accordion title={faq.question} answer={faq.answer} />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default Faq;
